/**
 * @fileoverview Hook responsável por autenticar o usuário, renovar a sessão e manter o contexto do usuário sincronizado.
 */

import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { openApi } from "../../services/api";
import usePermissionStore from "../../stores/usePermissionStore";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import moment from "moment";
import { isPublicPage, isSessionFreePage } from "../../utils/publicPages";
import decodeJwtPayload, { isOnboardingSession } from "../../utils/decodeJwtPayload";
import { clearPendingInvite, readPendingInvite, takeInviteReturn } from "../../utils/pendingInvite";
import { forgetSignupBrowser } from "../../components/AuthLayout/authHelpers";

// O acesso relido ao voltar para a aba (no máximo uma vez neste intervalo) e de tempos em tempos.
const ACCESS_RELOAD_ON_FOCUS_MS = 30 * 1000;
const ACCESS_RELOAD_EVERY_MS = 5 * 60 * 1000;

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  // Espelho do claim `impersonation` do JWT (ou null). Persistido no token,
  // então sobrevive a refresh sem precisar de storage separado.
  const [impersonation, setImpersonation] = useState(null);

  // Removido interceptors duplicados - agora estão no api.js

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    const token = localStorage.getItem("token");

    // Não tentar refresh nas telas públicas (a de confirmar o e-mail fica de
    // fora: o link pode ser clicado com a sessão aberta).
    if (isSessionFreePage()) {
      // Limpar qualquer token residual silenciosamente
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      setIsAuth(false);
      setLoading(false);
      return;
    }

    (async () => {
      if (token) {
        try {
          // Na confirmação do e-mail, a sessão vencida só some daqui: o api.js levaria
          // ao login no meio da confirmação, e o código já saiu do endereço.
          const { data } = await (isPublicPage() ? openApi : api).post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          localStorage.setItem("token", data.token);
          setIsAuth(true);
          setUser(data.user);
          // Recupera impersonação do JWT (sobrevive a F5)
          const decoded = decodeJwtPayload(data.token);
          setImpersonation(decoded?.impersonation || null);
        } catch (err) {
          // Se refresh falhar, limpar token e continuar
          localStorage.removeItem("token");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          setIsAuth(false);
          setImpersonation(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Sessão encerrada (sair, ou a releitura soube que ela acabou): fecha o
  // socket, limpa o que a aba guarda e volta ao login.
  const encerrarSessao = useCallback(() => {
    socketManager.fechar();
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    localStorage.removeItem("cshow");
    // O convite aberto nesta aba não fica para quem usar o navegador depois, nem o código do cadastro.
    clearPendingInvite();
    forgetSignupBrowser();
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});
    setImpersonation(null);
    if (history.location.pathname !== "/login") history.push("/login");
  }, [socketManager, history]);

  // Avisos de fora do React: o socket (auth_error) e o api.js (403 de
  // permissão). Os dois releem a sessão pelo refresh, que traz o usuário e o
  // token com o acesso de agora. O aviso de que o acesso mudou (access:changed,
  // do socket) também (relerSessao).
  const relerSessao = useRef(null);
  useEffect(() => {
    if (!isAuth) return;
    let lendo = false;
    // Pedida no meio de outra (que pode trazer o acesso de antes da mudança), sai de novo depois.
    let deNovo = false;
    let ultimaNegada = 0;
    const reler = async () => {
      if (lendo) {
        deNovo = true;
        return;
      }
      lendo = true;
      try {
        // Sem o interceptador do api.js: a resposta, 401 inclusive, fica aqui.
        const { data } = await openApi.post("/auth/refresh_token");
        localStorage.setItem("token", data.token);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        // Mesmo usuário: não troca o objeto (as telas não rodam de novo).
        setUser((atual) => (JSON.stringify(atual) === JSON.stringify(data.user) ? atual : data.user));
        // As permissões podem ter mudado sem mudar o usuário (uma exceção, o plano).
        usePermissionStore.getState().load();
      } catch (err) {
        // 401: a sessão acabou. Falha de rede ou do servidor não derruba a
        // sessão; o próximo aviso tenta de novo.
        if (err?.response?.status === 401) {
          deNovo = false;
          encerrarSessao();
        }
      } finally {
        lendo = false;
        if (deNovo) {
          deNovo = false;
          reler();
        }
      }
    };
    // Vários 403 seguidos (a tela pede várias coisas) viram uma leitura só.
    const aoNegar = () => {
      if (Date.now() - ultimaNegada < 15000) return;
      ultimaNegada = Date.now();
      reler();
    };
    relerSessao.current = reler;
    window.addEventListener("auth:error", reler);
    window.addEventListener("auth:forbidden", aoNegar);
    return () => {
      relerSessao.current = null;
      window.removeEventListener("auth:error", reler);
      window.removeEventListener("auth:forbidden", aoNegar);
    };
  }, [isAuth, encerrarSessao]);

  // O acesso da pessoa na empresa ativa (usePermissionStore): lido com a sessão
  // e de novo quando a empresa, o perfil ou a licença mudam; some ao sair.
  const { companyId: activeCompanyId, profile: activeProfile, accessScope: activeLicense } = user;
  useEffect(() => {
    // A sessão do onboarding (quem ainda não tem conta) não tem acesso para ler.
    // O token guardado é que diz: o super sem conta tem a sessão de sempre, e lê.
    if (!isAuth || isOnboardingSession()) {
      usePermissionStore.getState().clear();
      return;
    }
    usePermissionStore.getState().load();
  }, [isAuth, activeCompanyId, activeProfile, activeLicense]);

  // Uma opção da empresa pode mudar o acesso (o Resolver, quem vê as conversas,
  // a triagem): o servidor apaga o acesso guardado antes do aviso, e a tela o
  // relê. Também na reconexão, porque um aviso pode ter se perdido no caminho.
  // O acesso mudou e o servidor avisa (access:changed): o da pessoa (perfil,
  // licença, vínculo) relê a sessão (o usuário e o token de agora, com o
  // acesso); o da empresa (uma opção, o plano), só o acesso.
  useEffect(() => {
    if (!isAuth || !activeCompanyId) return undefined;
    const socket = socketManager.getSocket(activeCompanyId);
    const reload = () => usePermissionStore.getState().load();
    const onSettings = (data) => {
      if (data?.action === "update") reload();
    };
    const onAccessChanged = (data) => {
      if (data?.scope !== "company" && relerSessao.current) relerSessao.current();
      else reload();
    };
    socket.on(`company-${activeCompanyId}-settings`, onSettings);
    socket.on("ready", reload);
    socket.on("access:changed", onAccessChanged);
    return () => {
      socket.off(`company-${activeCompanyId}-settings`, onSettings);
      socket.off("ready", reload);
      socket.off("access:changed", onAccessChanged);
    };
  }, [isAuth, activeCompanyId, socketManager]);

  // Sem aviso nenhum (perdido numa queda longa), o acesso ainda é relido ao
  // voltar para a aba (no máximo a cada 30 segundos: a volta dispara o
  // visibilitychange e o focus) e a cada 5 minutos, com a aba à vista. Na
  // sessão do onboarding, não há acesso para reler.
  useEffect(() => {
    if (!isAuth || isOnboardingSession()) return undefined;
    let pedidaEm = 0;
    const reload = () => {
      pedidaEm = Date.now();
      usePermissionStore.getState().load();
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const ultima = Math.max(pedidaEm, usePermissionStore.getState().loadedAt);
      if (Date.now() - ultima >= ACCESS_RELOAD_ON_FOCUS_MS) reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, ACCESS_RELOAD_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer);
    };
  }, [isAuth, activeCompanyId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (companyId) {
      const socket = socketManager.getSocket(companyId);

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        socket.disconnect();
      };
    }
  }, [socketManager, user]);

  const finalizeSession = (data, { successText = i18n.t("auth.toasts.success"), replace = false } = {}) => {
    const {
      user: { companyId, id, company },
    } = data;

    if (has(company, "settings") && isArray(company.settings)) {
      const setting = company.settings.find(
        (s) => s.key === "campaignsEnabled"
      );
      if (setting && setting.value === "true") {
        localStorage.setItem("cshow", null);
      }
    }

    localStorage.setItem("token", data.token);
    // Quem ainda não tem conta (a sessão do onboarding) fica sem: com "null", o
    // socket abriria a sala de uma conta que não existe.
    localStorage.setItem("companyId", companyId ? String(companyId) : "");
    localStorage.setItem("userId", id);
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    // Quem foi ao login pelo convite ("Entrar para aceitar") volta para ele. A tela
    // muda antes da sessão: logada numa tela de entrada, a rota levaria ao app, que
    // montaria só para desmontar em seguida.
    const toInvite = takeInviteReturn() && !!readPendingInvite();
    if (toInvite) history.push("/invite");
    setUser(data.user);
    setIsAuth(true);
    const decoded = decodeJwtPayload(data.token);
    setImpersonation(decoded?.impersonation || null);
    toast.success(successText);
    // A raiz escolhe a tela inicial (getHomeRoute) com o acesso da pessoa já
    // lido: quem é "Apenas Interno", ou não vê as conversas, não abre /tickets.
    // Quem ainda não tem conta vai para a esteira, onde cria a Empresa e a conta.
    if (!toInvite) history[replace ? "replace" : "push"](data.onboarding || data.user?.onboarding ? "/comecar" : "/");
  };

  // Aplica um novo token (de impersonate/switch/return) sem redirect.
  // Forçamos reload da página: muitos componentes (sockets, listas, contexto
  // de Tickets) inicializam com companyId fixo no mount — sem reload o estado
  // antigo persiste e mistura tudo. Sem destino, a raiz escolhe a tela inicial.
  const applyTokenAndReload = (data, redirectTo = "/") => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("companyId", data.user.companyId);
    localStorage.setItem("userId", data.user.id);
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    window.location.assign(redirectTo);
  };

  /**
   * Super-admin entra no contexto de outro usuário sem fazer logout.
   * Backend embeda claim `impersonation` no JWT; após reload, useAuth detecta
   * isso e o ImpersonationBanner aparece automaticamente.
   */
  const impersonateUser = async (userId) => {
    try {
      const { data } = await api.post(`/auth/impersonate/${userId}`);
      applyTokenAndReload(data);
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  /**
   * Super-admin entra no contexto de outra Empresa (Conta) sem logout.
   * Não-destrutivo: o User.companyId no DB não muda, é só uma view de sessão.
   */
  const superSwitchCompany = async (companyId) => {
    try {
      const { data } = await api.post(`/auth/super-switch-company/${companyId}`);
      applyTokenAndReload(data);
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  /**
   * Troca a conta ativa de quem tem vínculo com mais de uma (o seletor "Conta
   * ativa" e o convite aceito): o servidor confere o vínculo, e a página abre na
   * tela inicial da conta nova. O erro volta para quem chamou.
   */
  const switchCompany = async (companyId) => {
    const { data } = await api.post("/auth/switch-company", { companyId });
    applyTokenAndReload(data);
  };

  /**
   * Lista as Contas da Empresa (groupName) atualmente ativa pro super que
   * está impersonando. Usado pelo ImpersonationBanner pra popular o controle
   * de navegação entre Contas (setinhas e dropdown).
   *
   * Retorna `{ contas, currentIndex, groupName }` ou null em erro.
   *
   * useCallback é OBRIGATÓRIO aqui: o ImpersonationBanner depende dessa
   * função no array de deps do useEffect. Sem useCallback, cada re-render
   * do AuthProvider cria nova referência → useEffect re-roda → o fetch
   * anterior é cancelado (cancelled=true) antes de chegar no setState →
   * `accounts` (no ImpersonationBanner) fica vazio pra sempre → setinhas de troca de Conta não
   * aparecem.
   */
  const listSiblingAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/impersonate/sibling-contas");
      return data;
    } catch (err) {
      // Silencioso — controle de troca de Conta simplesmente não aparece se
      // a chamada falhar (ex: super perdeu permissão entre tabs).
      return null;
    }
  }, []);

  /** Restaura sessão original do super-admin a partir do claim do JWT atual. */
  const returnFromImpersonation = async () => {
    try {
      const { data } = await api.post("/auth/impersonate/return");
      applyTokenAndReload(data, "/companies");
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);

      if (data.mfaRequired) {
        setLoading(false);
        return { mfaRequired: true, mfaTicket: data.mfaTicket };
      }
      if (data.mfaSetupRequired) {
        setLoading(false);
        return { mfaSetupRequired: true, setupTicket: data.setupTicket };
      }

      finalizeSession(data);
      setLoading(false);
      return { ok: true, mfaGraceDaysLeft: data.mfaGraceDaysLeft };
    } catch (err) {
      toastError(err);
      setLoading(false);
      return { error: true };
    }
  };

  const handleLoginMfa = async ({ mfaTicket, otp, backupCode }) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/mfa", {
        mfaTicket,
        otp,
        backupCode,
      });
      finalizeSession(data);
      setLoading(false);
      return { ok: true };
    } catch (err) {
      toastError(err);
      setLoading(false);
      return { error: true };
    }
  };

  const finalizeMfaSetupSession = (data) => {
    finalizeSession(data);
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // A sessão do onboarding (sem conta) se encerra pela rota dela.
      await api.delete(isOnboardingSession() ? "/onboarding/setup/session" : "/auth/logout");
      encerrarSessao();
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      // Não mostrar erro para getCurrentUserInfo, apenas retornar null
      // Isso evita logout desnecessário
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    impersonation,
    handleLogin,
    handleLoginMfa,
    finalizeMfaSetupSession,
    startSession: finalizeSession,
    handleLogout,
    getCurrentUserInfo,
    impersonateUser,
    superSwitchCompany,
    switchCompany,
    listSiblingAccounts,
    returnFromImpersonation,
  };
};

export default useAuth;
